import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { storeReferralCode, getStoredReferralCode, trackReferral, clearStoredReferralCode } from '@/lib/referralUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Loader2, Mail, BookOpen } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { AuthDebug } from '@/components/AuthDebug';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const passwordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type EmailForm = z.infer<typeof emailSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export default function Auth() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [mode, setMode] = useState<'signup' | 'signin' | 'reset'>('signup');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [usePassword, setUsePassword] = useState(false);

  // Capture referral code and mode from URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      storeReferralCode(refCode);
    }
    
    const urlMode = searchParams.get('mode');
    if (urlMode === 'signin' || urlMode === 'signup') {
      setMode(urlMode);
    }
  }, [searchParams]);

  const emailForm = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Redirect if already authenticated and process referral
  useEffect(() => {
    if (user && !authLoading) {
      // Check for stored referral code and track it
      const storedRef = getStoredReferralCode();
      if (storedRef) {
        trackReferral(storedRef, user.id).then((success) => {
          if (success) {
            clearStoredReferralCode();
            toast({
              title: 'Welcome to Dino!',
              description: 'You were invited by a friend. Enjoy reading together!',
            });
          }
        });
      }
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Early return while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Redirect authenticated users
  if (user) {
    return null;
  }


  const handleMagicLink = async (data: EmailForm) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: data.email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      
      if (error) throw error;

      setMagicLinkSent(true);
      toast({
        title: "Magic link sent!",
        description: "Check your email for a sign-in link.",
      });
    } catch (error: any) {
      console.error('Magic link error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send magic link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (data: EmailForm) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/auth?mode=update-password`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: "Password reset email sent!",
        description: "Check your inbox for instructions to reset your password.",
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordAuth = async (data: PasswordForm, mode: 'signIn' | 'signUp') => {
    setLoading(true);
    try {
      let result;
      
      if (mode === 'signUp') {
        result = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
      } else {
        result = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        });
      }
      
      if (result.error) throw result.error;

      if (mode === 'signUp') {
        toast({
          title: "Account created!",
          description: "Please check your email to verify your account.",
        });
      } else {
        // For sign in, try to ensure profile exists
        if (result.data.user) {
          try {
            await supabase.rpc('ensure_user_profile');
          } catch (profileError: any) {
            console.error('Profile creation error during sign in:', profileError);
            toast({
              title: "Profile Setup Warning",
              description: `Profile creation issue: ${profileError.message}`,
              variant: "destructive",
            });
          }
        }
      }
    } catch (error: any) {
      console.error('Password auth error:', error);
      
      // Show specific error details for diagnostics
      let description = error.message || `Failed to ${mode === 'signUp' ? 'sign up' : 'sign in'}. Please try again.`;
      
      // Add error code for debugging if available
      if (error.code) {
        description = `${error.code}: ${description}`;
      }
      
      // Handle specific error cases
      if (error.message?.includes('duplicate key value violates unique constraint')) {
        description = "Database constraint error: This might be due to duplicate data. Please try again or contact support.";
      } else if (error.message?.includes('shelves_user_id_name_key')) {
        description = "Database error: Duplicate shelf creation detected. This has been logged for investigation.";
      } else if (error.code === 'unexpected_failure') {
        description = "Unexpected database error occurred. Please try again in a moment.";
      }

      toast({
        title: "Authentication Error",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md card-elevated" role="main" aria-label="Authentication form">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center" aria-hidden="true">
            <BookOpen className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-heading">
              {mode === 'signup' && 'Start Your Reading Journey 📚'}
              {mode === 'signin' && 'Welcome Back! 👋'}
              {mode === 'reset' && 'Reset Your Password 🔐'}
            </CardTitle>
            <CardDescription>
              {mode === 'signup' && 'Join thousands of readers tracking their books, building streaks, and discovering their next favorite read'}
              {mode === 'signin' && 'Sign in to continue your reading adventure'}
              {mode === 'reset' && 'Enter your email address and we\'ll send you a link to reset your password'}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Password Reset Flow */}
          {mode === 'reset' && (
            <>
              {resetEmailSent ? (
                <div className="text-center space-y-4 animate-fade-in">
                  <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">Check your email</h3>
                    <p className="text-sm text-muted-foreground">
                      We sent password reset instructions to {emailForm.getValues('email')}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setResetEmailSent(false);
                      setMode('signin');
                    }}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={emailForm.handleSubmit(handlePasswordReset)} className="space-y-4 animate-fade-in" aria-label="Password reset form">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="Enter your email"
                      {...emailForm.register('email')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={emailForm.formState.errors.email ? "reset-email-error" : undefined}
                    />
                    {emailForm.formState.errors.email && (
                      <p id="reset-email-error" className="text-sm text-destructive" role="alert">
                        {emailForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12"
                    disabled={loading}
                    aria-label="Send password reset link"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setMode('signin');
                      emailForm.reset();
                    }}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Sign Up Flow - Magic Link */}
          {mode === 'signup' && (
            <>
              {magicLinkSent ? (
                <div className="text-center space-y-4 animate-fade-in">
                  <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">Check your email</h3>
                    <p className="text-sm text-muted-foreground">
                      We sent a magic link to {emailForm.getValues('email')}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => setMagicLinkSent(false)}
                    className="w-full"
                  >
                    Send another link
                  </Button>
                </div>
              ) : (
                <form onSubmit={emailForm.handleSubmit(handleMagicLink)} className="space-y-4 animate-fade-in" aria-label="Sign up form">
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email address</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="Enter your email"
                      {...emailForm.register('email')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={emailForm.formState.errors.email ? "signup-email-error" : undefined}
                    />
                    {emailForm.formState.errors.email && (
                      <p id="signup-email-error" className="text-sm text-destructive" role="alert">
                        {emailForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12"
                    disabled={loading}
                    aria-label="Continue with email sign up"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Continue with Email
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    No password needed - we'll send you a magic link
                  </p>
                </form>
              )}
            </>
          )}

          {/* Sign In Flow - Magic Link or Password */}
          {mode === 'signin' && (
            <>
              {!usePassword ? (
                <>
                  {magicLinkSent ? (
                    <div className="text-center space-y-4 animate-fade-in">
                      <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Check your email</h3>
                        <p className="text-sm text-muted-foreground">
                          We sent a magic link to {emailForm.getValues('email')}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => setMagicLinkSent(false)}
                        className="w-full"
                      >
                        Send another link
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={emailForm.handleSubmit(handleMagicLink)} className="space-y-4 animate-fade-in" aria-label="Sign in form">
                      <div className="space-y-2">
                        <Label htmlFor="signin-email">Email address</Label>
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="Enter your email"
                          {...emailForm.register('email')}
                          disabled={loading}
                          aria-required="true"
                          aria-describedby={emailForm.formState.errors.email ? "signin-email-error" : undefined}
                        />
                        {emailForm.formState.errors.email && (
                          <p id="signin-email-error" className="text-sm text-destructive" role="alert">
                            {emailForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-12"
                        disabled={loading}
                        aria-label="Continue with email sign in"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Mail className="w-5 h-5 mr-2" />
                            Continue with Email
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        No password needed - we'll send you a magic link
                      </p>

                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setUsePassword(true)}
                        className="w-full text-xs"
                      >
                        Or sign in with password
                      </Button>
                    </form>
                  )}
                </>
              ) : (
                <form 
                  onSubmit={passwordForm.handleSubmit((data) => handlePasswordAuth(data, 'signIn'))} 
                  className="space-y-4 animate-fade-in"
                  aria-label="Sign in with password form"
                >
                  <div className="space-y-2">
                    <Label htmlFor="signin-password-email">Email address</Label>
                    <Input
                      id="signin-password-email"
                      type="email"
                      placeholder="Enter your email"
                      {...passwordForm.register('email')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={passwordForm.formState.errors.email ? "signin-password-email-error" : undefined}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="Enter your password"
                      {...passwordForm.register('password')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={passwordForm.formState.errors.password ? "signin-password-error" : undefined}
                    />
                  </div>

                  {passwordForm.formState.errors.email && (
                    <p id="signin-password-email-error" className="text-sm text-destructive" role="alert">
                      {passwordForm.formState.errors.email.message}
                    </p>
                  )}

                  {passwordForm.formState.errors.password && (
                    <p id="signin-password-error" className="text-sm text-destructive" role="alert">
                      {passwordForm.formState.errors.password.message}
                    </p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12"
                    disabled={loading}
                    aria-label="Sign in to your account"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  <p className="text-xs text-center">
                    <button
                      type="button"
                      onClick={() => {
                        const email = passwordForm.getValues('email');
                        if (email) {
                          emailForm.setValue('email', email);
                        }
                        setMode('reset');
                        passwordForm.reset();
                      }}
                      className="text-primary hover:underline"
                      aria-label="Forgot password? Reset it here"
                    >
                      Forgot password?
                    </button>
                  </p>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setUsePassword(false)}
                    className="w-full text-xs"
                  >
                    Or use magic link instead
                  </Button>
                </form>
              )}
            </>
          )}

          {mode !== 'reset' && (
            <>
              <Separator role="presentation" />

              {/* Mode Toggle */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  {mode === 'signup' ? 'Already have an account?' : 'New to Dino?'}
                </p>
                <Button
                  variant="link"
                  onClick={() => {
                    setMode(mode === 'signup' ? 'signin' : 'signup');
                    setMagicLinkSent(false);
                    setResetEmailSent(false);
                    emailForm.reset();
                    passwordForm.reset();
                  }}
                  className="text-primary font-semibold"
                  aria-label={mode === 'signup' ? 'Switch to sign in' : 'Switch to sign up'}
                >
                  {mode === 'signup' ? 'Sign in instead' : 'Create an account'}
                </Button>
              </div>
            </>
          )}

          <div className="text-xs text-center text-muted-foreground">
            By continuing, you agree to our{' '}
            <a href="/terms-of-service" className="underline hover:text-foreground" aria-label="Read our Terms of Service">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" className="underline hover:text-foreground" aria-label="Read our Privacy Policy">
              Privacy Policy
            </a>
          </div>
        </CardContent>
      </Card>
      
      {/* Development debug panel */}
      <AuthDebug />
    </div>
  );
}